import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useProject } from '@/hooks/useProject';
import { useTasksByProject } from '@/hooks/useTasks';
import {
  PHASE_TYPE_LABELS,
  PHASE_TYPE_COLORS,
  IProjectPhase,
  PhaseType,
} from '@/types/project';
import { ITask, TASK_STATUS_COLORS, TASK_STATUS_LABELS } from '@/types/task';
import ProjectTaskModal from './ProjectTaskModal';

interface ProjectGanttProps {
  projectId: string;
}

// Helper to parse date string to Date
const parseDate = (dateStr: string): Date | null => {
  if (!dateStr) return null;
  return new Date(dateStr);
};

// Helper to format date for display
const formatDate = (dateStr: string): string => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString();
};

// Calculate the difference in days between two dates
const daysBetween = (start: Date, end: Date): number => {
  const oneDay = 24 * 60 * 60 * 1000;
  return Math.round((end.getTime() - start.getTime()) / oneDay);
};

// Add days to a date
const addDays = (date: Date, days: number): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

interface GanttTask {
  task: ITask;
  phase: IProjectPhase | null;
  startDate: Date;
  endDate: Date;
  durationDays: number;
}

const ProjectGantt: React.FC<ProjectGanttProps> = ({ projectId }) => {
  const { t } = useTranslation('project');
  const { data, isLoading, error } = useProject(projectId);
  const { data: tasksData, refetch: refetchTasks } = useTasksByProject(projectId);

  // Task Modal state
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [selectedPhaseForTask, setSelectedPhaseForTask] = useState<{ id: string; name: string } | null>(null);
  const [editingTask, setEditingTask] = useState<ITask | null>(null);

  const project = data?.project;
  const phases = data?.phases || [];
  const allTasks = useMemo(() => tasksData?.tasks || [], [tasksData]);

  // Process tasks and calculate Gantt data
  const ganttData = useMemo(() => {
    if (!allTasks.length) return { tasks: [], minDate: new Date(), maxDate: new Date(), totalDays: 0 };

    // Filter tasks with valid dates and sort chronologically
    const validTasks: GanttTask[] = allTasks
      .filter(task => task.planned_start && task.planned_end)
      .map(task => {
        const startDate = parseDate(task.planned_start)!;
        const endDate = parseDate(task.planned_end)!;
        const phase = phases.find(p => p.id === task.phase_id) || null;
        return {
          task,
          phase,
          startDate,
          endDate,
          durationDays: Math.max(1, daysBetween(startDate, endDate) + 1),
        };
      })
      .sort((a, b) => a.startDate.getTime() - b.startDate.getTime());

    if (!validTasks.length) {
      return { tasks: [], minDate: new Date(), maxDate: new Date(), totalDays: 0 };
    }

    // Calculate date range
    const minDate = validTasks.reduce((min, t) => t.startDate < min ? t.startDate : min, validTasks[0].startDate);
    const maxDate = validTasks.reduce((max, t) => t.endDate > max ? t.endDate : max, validTasks[0].endDate);
    const totalDays = daysBetween(minDate, maxDate) + 1;

    return { tasks: validTasks, minDate, maxDate, totalDays };
  }, [allTasks, phases]);

  // Generate date headers (weeks/days depending on range)
  const dateHeaders = useMemo(() => {
    const { minDate, totalDays } = ganttData;
    if (totalDays === 0) return [];

    const headers: { date: Date; label: string; isWeekStart: boolean }[] = [];
    for (let i = 0; i < totalDays; i++) {
      const date = addDays(minDate, i);
      const isWeekStart = date.getDay() === 1; // Monday
      headers.push({
        date,
        label: date.getDate().toString(),
        isWeekStart,
      });
    }
    return headers;
  }, [ganttData]);

  // Generate month headers
  const monthHeaders = useMemo(() => {
    const { minDate, totalDays } = ganttData;
    if (totalDays === 0) return [];

    const months: { label: string; startIndex: number; span: number }[] = [];
    let currentMonth = -1;
    let currentYear = -1;

    for (let i = 0; i < totalDays; i++) {
      const date = addDays(minDate, i);
      const month = date.getMonth();
      const year = date.getFullYear();

      if (month !== currentMonth || year !== currentYear) {
        if (months.length > 0) {
          months[months.length - 1].span = i - months[months.length - 1].startIndex;
        }
        months.push({
          label: date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
          startIndex: i,
          span: 1,
        });
        currentMonth = month;
        currentYear = year;
      }
    }

    if (months.length > 0) {
      months[months.length - 1].span = totalDays - months[months.length - 1].startIndex;
    }

    return months;
  }, [ganttData]);

  const handleEditTask = (task: ITask) => {
    const phase = phases.find(p => p.id === task.phase_id);
    if (phase) {
      setSelectedPhaseForTask({ id: phase.id, name: phase.phase_name });
      setEditingTask(task);
      setShowTaskModal(true);
    }
  };

  const handleCloseTaskModal = () => {
    setShowTaskModal(false);
    setSelectedPhaseForTask(null);
    setEditingTask(null);
    refetchTasks();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-red-500 dark:text-red-400">
        <p>{t('error.loadProject')}</p>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        <p>{t('error.projectNotFound')}</p>
      </div>
    );
  }

  const { tasks: ganttTasks, minDate, totalDays } = ganttData;
  const dayWidth = 32; // pixels per day
  const rowHeight = 48; // pixels per row

  if (ganttTasks.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500 dark:text-gray-400">
        <svg className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
        <p className="text-lg font-medium mb-2">{t('gantt.noTasks', 'No tasks with dates')}</p>
        <p className="text-sm">{t('gantt.addTasksHint', 'Add tasks with planned start and end dates to see the Gantt chart')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Project Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{project.project_name}</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('gantt.dateRange', 'Date range')}: {formatDate(minDate.toISOString())} - {formatDate(addDays(minDate, totalDays - 1).toISOString())}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {ganttTasks.length} {t('tasks.title').toLowerCase()}
          </span>
        </div>
      </div>

      {/* Gantt Chart Container */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <div className="min-w-max">
            {/* Header */}
            <div className="flex border-b border-gray-200 dark:border-gray-700">
              {/* Task Name Column */}
              <div className="w-64 flex-shrink-0 bg-gray-50 dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700">
                <div className="h-12 flex items-center px-4 font-medium text-sm text-gray-700 dark:text-gray-300">
                  {t('tasks.columns.name')}
                </div>
              </div>

              {/* Timeline Header */}
              <div className="flex-1">
                {/* Month Headers */}
                <div className="flex h-6 bg-gray-100 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                  {monthHeaders.map((month, idx) => (
                    <div
                      key={idx}
                      className="text-xs font-medium text-gray-600 dark:text-gray-400 flex items-center justify-center border-r border-gray-200 dark:border-gray-700"
                      style={{ width: month.span * dayWidth }}
                    >
                      {month.label}
                    </div>
                  ))}
                </div>

                {/* Day Headers */}
                <div className="flex h-6 bg-gray-50 dark:bg-gray-800">
                  {dateHeaders.map((header, idx) => (
                    <div
                      key={idx}
                      className={`text-xs text-center text-gray-500 dark:text-gray-400 flex items-center justify-center border-r border-gray-100 dark:border-gray-700 ${
                        header.isWeekStart ? 'border-l-2 border-l-gray-300 dark:border-l-gray-600' : ''
                      } ${header.date.getDay() === 0 || header.date.getDay() === 6 ? 'bg-gray-100 dark:bg-gray-700' : ''}`}
                      style={{ width: dayWidth }}
                    >
                      {header.label}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Task Rows */}
            <div>
              {ganttTasks.map((ganttTask) => {
                const { task, phase, startDate, durationDays } = ganttTask;
                const phaseColors = phase ? PHASE_TYPE_COLORS[phase.phase_type as PhaseType] : PHASE_TYPE_COLORS.custom;
                const startOffset = daysBetween(minDate, startDate);
                const barLeft = startOffset * dayWidth;
                const barWidth = durationDays * dayWidth;

                return (
                  <div
                    key={task.id}
                    className={`flex border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${phaseColors.bg}`}
                    style={{ height: rowHeight }}
                  >
                    {/* Task Name */}
                    <div
                      className={`w-64 flex-shrink-0 border-r ${phaseColors.border} flex items-center px-4 cursor-pointer`}
                      onClick={() => handleEditTask(task)}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${TASK_STATUS_COLORS[task.status]}`}>
                            {TASK_STATUS_LABELS[task.status]}
                          </span>
                          <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {task.task_name}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {phase ? PHASE_TYPE_LABELS[phase.phase_type as PhaseType] : '-'} • {task.task_owner}
                        </div>
                      </div>
                    </div>

                    {/* Timeline Bar */}
                    <div className="flex-1 relative">
                      {/* Grid lines */}
                      <div className="absolute inset-0 flex">
                        {dateHeaders.map((header, i) => (
                          <div
                            key={i}
                            className={`border-r border-gray-100 dark:border-gray-700 ${
                              header.isWeekStart ? 'border-l border-l-gray-200 dark:border-l-gray-600' : ''
                            } ${header.date.getDay() === 0 || header.date.getDay() === 6 ? 'bg-gray-50/50 dark:bg-gray-700/30' : ''}`}
                            style={{ width: dayWidth }}
                          />
                        ))}
                      </div>

                      {/* Task Bar */}
                      <div
                        className="absolute top-2 bottom-2 rounded cursor-pointer transition-all hover:scale-y-110 shadow-sm"
                        style={{
                          left: barLeft,
                          width: Math.max(barWidth, dayWidth),
                          backgroundColor: phaseColors.dark,
                        }}
                        onClick={() => handleEditTask(task)}
                        title={`${task.task_name}\n${formatDate(task.planned_start)} - ${formatDate(task.planned_end)}\n${durationDays} days`}
                      >
                        <div className="h-full flex items-center px-2 overflow-hidden">
                          <span className="text-xs font-medium text-white truncate">
                            {barWidth > 80 ? task.task_name : ''}
                          </span>
                        </div>
                        {/* Progress indicator for done tasks */}
                        {task.status === 'done' && (
                          <div className="absolute inset-0 bg-white/20 rounded" />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <span className="font-medium text-gray-700 dark:text-gray-300">{t('gantt.legend', 'Phase Colors')}:</span>
        {Object.entries(PHASE_TYPE_LABELS).slice(0, 8).map(([type, label]) => {
          const colors = PHASE_TYPE_COLORS[type as PhaseType];
          return (
            <div key={type} className="flex items-center gap-1.5">
              <div
                className="w-4 h-4 rounded"
                style={{ backgroundColor: colors.dark }}
              />
              <span className="text-gray-600 dark:text-gray-400">{label}</span>
            </div>
          );
        })}
      </div>

      {/* Task Modal */}
      {showTaskModal && selectedPhaseForTask && (
        <ProjectTaskModal
          isOpen={showTaskModal}
          onClose={handleCloseTaskModal}
          task={editingTask}
          allTasks={allTasks}
          projectId={projectId}
          projectName={project.project_name}
          phaseId={selectedPhaseForTask.id}
          phaseName={selectedPhaseForTask.name}
        />
      )}
    </div>
  );
};

export default ProjectGantt;
